import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FamilyMember } from '../../api/families';
import type { MedicalDocument, MemberDocumentFilterOptions } from '../../api/documents';
import { memberDisplayInitials, memberDisplayName } from '../../utils/memberDisplay';
import { avatarGradient } from './memberAvatar';
import MemberStatCard from './MemberStatCard';
import type { MemberTabId } from './MemberTabs';

interface MemberHeroProps {
  member: FamilyMember;
  documents: MedicalDocument[];
  filterOptions: MemberDocumentFilterOptions | null;
  isAdvanced: boolean;
  /** Same flag the bar uses — gates the "Vezi familia" link inside the
   *  details accordion since shared-member viewers can't reach the
   *  family page. */
  viewedAsSharedMember: boolean;
  onUpload: () => void;
  /** Switch active tab (used by the timeline stat CTA). */
  onSwitchTab: (tab: MemberTabId) => void;
}

const genderLabel: Record<string, string> = {
  male: 'Masculin',
  female: 'Feminin',
  other: 'Altul',
};

const genderIcon: Record<string, React.ReactNode> = {
  male: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="10" cy="14" r="6" />
      <path d="m20 4-6.5 6.5" />
      <path d="M14 4h6v6" />
    </svg>
  ),
  female: (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="9" r="6" />
      <path d="M12 15v6" />
      <path d="M9 18h6" />
    </svg>
  ),
};

function ageFromDob(iso: string | null): number | null {
  if (!iso) return null;
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  return age >= 0 && age < 200 ? age : null;
}

function relativeRomanian(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Date.now() - t;
  if (diff < 0) return null;
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'Acum';
  if (min < 60) return `Acum ${min} min`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `Acum ${hr} ${hr === 1 ? 'oră' : 'ore'}`;
  const days = Math.round(hr / 24);
  if (days < 30) return `Acum ${days} ${days === 1 ? 'zi' : 'zile'}`;
  const months = Math.round(days / 30);
  if (months < 12) return `Acum ${months} ${months === 1 ? 'lună' : 'luni'}`;
  const years = Math.round(months / 12);
  return `Acum ${years} ${years === 1 ? 'an' : 'ani'}`;
}

function newestDocUploadIso(documents: MedicalDocument[]): string | null {
  let latest: number = 0;
  let latestIso: string | null = null;
  for (const d of documents) {
    if (!d.upload_date) continue;
    const t = new Date(d.upload_date).getTime();
    if (!Number.isNaN(t) && t > latest) {
      latest = t;
      latestIso = d.upload_date;
    }
  }
  return latestIso;
}

const IconChevronDown = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const IconDocs = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconAlert = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const IconActivity = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const MemberHero: React.FC<MemberHeroProps> = ({
  member,
  documents,
  filterOptions,
  isAdvanced,
  viewedAsSharedMember,
  onUpload,
  onSwitchTab,
}) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const initials = memberDisplayInitials(member);
  const displayName = memberDisplayName(member);
  const gradient = useMemo(() => avatarGradient(member.id), [member.id]);
  const familyLabel = (member.family_name || '').trim() || 'Familie';

  const docCount = filterOptions?.document_count ?? documents.length;
  const totalEvents = member.timeline?.total_events ?? null;
  const importantEvents = useMemo(() => {
    const events = member.timeline?.events ?? [];
    return events.filter((e) => e.severity === 'important' || e.severity === 'critical').length;
  }, [member.timeline]);

  // "Last activity" = max of timeline.generated_at, last upload_date, member.updated_at.
  const lastActivityIso = useMemo(() => {
    const candidates: Array<string | null | undefined> = [
      member.timeline?.generated_at ?? null,
      newestDocUploadIso(documents),
      member.updated_at,
    ];
    let bestIso: string | null = null;
    let best = 0;
    for (const c of candidates) {
      if (!c) continue;
      const t = new Date(c).getTime();
      if (!Number.isNaN(t) && t > best) {
        best = t;
        bestIso = c;
      }
    }
    return bestIso;
  }, [member, documents]);

  const lastActivityRel = relativeRomanian(lastActivityIso);

  // "Recent uploads delta" — naive 7-day window using upload_date strings.
  const uploadsLastWeek = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    let n = 0;
    for (const d of documents) {
      if (!d.upload_date) continue;
      const t = new Date(d.upload_date).getTime();
      if (!Number.isNaN(t) && t >= cutoff) n++;
    }
    return n;
  }, [documents]);

  const age = ageFromDob(member.date_of_birth);

  return (
    <>
      {/* Hero block (NOT sticky). The sticky page-bar lives in
          `MemberBar` (which composes the reusable `<PageBar>`) and is
          rendered as a sibling of the page-container in
          `MemberDetail.tsx` so it spans the full viewport. */}
      <header className="member-hero">
        <div className="member-hero-id">
          <div
            className="member-hero-avatar"
            style={{ background: gradient }}
            aria-hidden
          >
            {initials}
          </div>
          <div className="member-hero-text">
            <h1 className="member-hero-name">{displayName}</h1>
            <div className="member-hero-meta">
              {member.member_relationship && (
                <span className="member-hero-meta-item">{member.member_relationship}</span>
              )}
              {member.member_relationship && age != null && <span className="member-hero-meta-sep">·</span>}
              {age != null && (
                <span className="member-hero-meta-item">
                  {age} ani
                </span>
              )}
              {(member.member_relationship || age != null) && member.gender && (
                <span className="member-hero-meta-sep">·</span>
              )}
              {member.gender && (
                <span className="member-hero-meta-item" title={genderLabel[member.gender] || member.gender}>
                  {genderIcon[member.gender] || null}
                  {genderLabel[member.gender] || member.gender}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="member-stats" role="list">
          <MemberStatCard
            tone="teal"
            icon={<IconDocs />}
            label="Documente"
            value={docCount}
            delta={
              uploadsLastWeek > 0 ? (
                <span className="member-stat-delta-positive">
                  +{uploadsLastWeek} în ultima săptămână
                </span>
              ) : (
                'Niciun upload nou'
              )
            }
          />
          <MemberStatCard
            tone="blue"
            icon={<IconClock />}
            label="Cronologie"
            value={totalEvents != null ? totalEvents : undefined}
            emptyLabel="Negenerată"
            delta={totalEvents != null ? (totalEvents === 1 ? 'eveniment' : 'evenimente') : undefined}
            ctaLabel={totalEvents == null ? 'Generează cronologie' : undefined}
            onCtaClick={
              totalEvents == null ? () => onSwitchTab('timeline') : undefined
            }
          />
          <MemberStatCard
            tone={importantEvents > 0 ? 'amber' : 'slate'}
            icon={<IconAlert />}
            label="Constatări importante"
            value={importantEvents}
            delta={
              importantEvents === 0
                ? 'Niciuna identificată'
                : importantEvents === 1
                  ? 'eveniment marcat'
                  : 'evenimente marcate'
            }
          />
          <MemberStatCard
            tone="slate"
            icon={<IconActivity />}
            label="Ultima activitate"
            value={lastActivityRel ?? undefined}
            emptyLabel="Fără activitate"
          />
        </div>

        {/* Collapsible "Detalii membru" */}
        <div className="member-details">
          <button
            type="button"
            className="member-details-toggle"
            aria-expanded={detailsOpen}
            aria-controls="member-details-grid"
            onClick={() => setDetailsOpen((o) => !o)}
          >
            <span>{detailsOpen ? 'Ascunde detaliile' : 'Vezi detalii membru'}</span>
            <IconChevronDown />
          </button>
          {detailsOpen && (
            <div className="member-details-grid" id="member-details-grid">
              <div>
                <div className="member-details-field-label">Familie</div>
                <div className="member-details-field-value">
                  {viewedAsSharedMember ? (
                    <span title="Ai acces doar la acest membru, nu la întreaga familie">
                      {familyLabel}
                    </span>
                  ) : (
                    <Link to={`/families/${member.family_id}`}>
                      {isAdvanced ? `Vezi familia #${member.family_id}` : 'Vezi familia'}
                    </Link>
                  )}
                </div>
              </div>
              {member.date_of_birth && (
                <div>
                  <div className="member-details-field-label">Data nașterii</div>
                  <div className="member-details-field-value">
                    {new Date(member.date_of_birth).toLocaleDateString('ro-RO')}
                  </div>
                </div>
              )}
              {member.gender && (
                <div>
                  <div className="member-details-field-label">Gen</div>
                  <div className="member-details-field-value">
                    {genderLabel[member.gender] || member.gender}
                  </div>
                </div>
              )}
              {member.member_relationship && (
                <div>
                  <div className="member-details-field-label">Relație</div>
                  <div className="member-details-field-value">{member.member_relationship}</div>
                </div>
              )}
              <div>
                <div className="member-details-field-label">Documente</div>
                <div className="member-details-field-value">{filterOptions?.document_count ?? '—'}</div>
              </div>
              {member.notes && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <div className="member-details-field-label">Note</div>
                  <div className="member-details-field-value">{member.notes}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default MemberHero;
